import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SERVICES_API } from '@pro-connect/shared';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ProEducationTrainingService } from './pro-education-training.service';
import { IProEducationTraining } from './models/pro-education-training';
import { jsonToHtml } from 'shared/src/utils/helpers/xss';
import { IProEducationEvent } from './models/pro-education-event';
import { format, isAfter } from 'date-fns';

export class ProEducationTrainingStateGetTrainingDetails {
  public static readonly type = '[ProEducationTrainingState] get training';

  constructor(public payload: { slug: string }) {}
}

export class ProEducationTrainingStateMarkEvent {
  public static readonly type = '[ProEducationTrainingState] mark event';

  constructor(public payload: { event: IProEducationEvent }) {}
}

export interface ProEducationTrainingStateModel {
  loading: boolean;
  training: IProEducationTraining | null;
  markedEvent: IProEducationEvent | null;
}

@State<ProEducationTrainingStateModel>({
  name: 'proEducationTraining',
  defaults: {
    loading: false,
    training: null,
    markedEvent: null,
  },
})
@Injectable()
export class ProEducationTrainingState {
  public constructor(
    private readonly _proEducationTrainingService: ProEducationTrainingService,
    @Inject(SERVICES_API)
    private readonly _servicesApi: string,
  ) {}

  @Selector()
  public static loading({ loading }: ProEducationTrainingStateModel): boolean {
    return loading;
  }

  @Selector()
  public static training({ training }: ProEducationTrainingStateModel): IProEducationTraining | null {
    return training;
  }

  @Selector()
  public static markedEvent({ markedEvent }: ProEducationTrainingStateModel): IProEducationEvent | null {
    return markedEvent;
  }

  @Action(ProEducationTrainingStateGetTrainingDetails)
  public getTrainings(
    state: StateContext<ProEducationTrainingStateModel>,
    { payload: { slug } }: ProEducationTrainingStateGetTrainingDetails,
  ): Observable<any> {
    const { patchState } = state;

    patchState({
      loading: true,
    });

    return this._proEducationTrainingService.getTrainingDetails(slug).pipe(
      tap((training: IProEducationTraining) => {
        patchState({
          training: {
            ...training,
            description: training.description
              ? jsonToHtml(training.description).replace(new RegExp('/uploads/', 'g'), `${this._servicesApi}/uploads/`)
              : null,
            information: training.information
              ? jsonToHtml(training.information).replace(new RegExp('/uploads/', 'g'), `${this._servicesApi}/uploads/`)
              : null,
            price: Math.round(training.price),
            events: training.events.filter((e: IProEducationEvent) =>
              isAfter(new Date(format(new Date(e.start_date), 'yyyy-MM-dd HH:mm')), new Date()),
            ),
          },
        });
      }),
      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }

  @Action(ProEducationTrainingStateMarkEvent)
  public markEvent(
    state: StateContext<ProEducationTrainingStateModel>,
    { payload: { event } }: ProEducationTrainingStateMarkEvent,
  ): void {
    const { patchState } = state;
    patchState({
      markedEvent: event,
    });
    const int = setInterval(() => {
      patchState({
        markedEvent: null,
      });
      clearInterval(int);
    }, 3000);
  }
}
